import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Send } from "lucide-react";
import api, { errMsg, money } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function Invoices() {
  const [items, setItems] = useState([]);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  const load = () => api.get("/invoices").then(({ data }) => setItems(data)).catch((e) => setError(errMsg(e)));
  useEffect(() => { load(); }, []);

  const unpaid = items.filter((i) => i.status === "unpaid").reduce((s, i) => s + i.amount, 0);

  return (
    <div data-testid="invoices-page" className="space-y-8">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.28em] text-gold">Billing</p>
        <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-50">Invoices</h1>
        <p className="mt-2 text-sm text-slate-400">
          Completing a trip issues a branded invoice and emails it to the customer. Unpaid invoices are
          automatically reminded at 3 and 7 days.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div data-testid="invoice-stat-open" className="rounded-xl border border-ink-600 bg-ink-800 p-6">
          <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">Outstanding</p>
          <p className="mt-1.5 font-display text-2xl font-semibold text-slate-50">{money(unpaid)}</p>
        </div>
        <div className="rounded-xl border border-ink-600 bg-ink-800 p-6">
          <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">Invoices</p>
          <p className="mt-1.5 font-display text-2xl font-semibold text-slate-50">{items.length}</p>
        </div>
        <div className="rounded-xl border border-ink-600 bg-ink-800 p-6">
          <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">Paid</p>
          <p className="mt-1.5 font-display text-2xl font-semibold text-slate-50">
            {items.filter((i) => i.status === "paid").length}
          </p>
        </div>
      </div>

      {msg && <p data-testid="invoice-message" className="text-sm text-emerald-400">{msg}</p>}
      {error && <p data-testid="invoice-error" className="text-sm text-red-400">{error}</p>}

      <div className="overflow-hidden rounded-xl border border-ink-600 bg-ink-800">
        {items.length === 0 && (
          <p data-testid="invoices-empty" className="px-5 py-10 text-center text-sm text-slate-500">
            No invoices yet — mark a trip complete to issue one.
          </p>
        )}
        {items.map((i) => (
          <div key={i.id} data-testid={`invoice-row-${i.number}`} className="flex flex-col gap-3 border-b border-ink-600 px-5 py-4 last:border-0 sm:flex-row sm:items-center sm:justify-between">
            <Link to={`/app/invoices/${i.id}`} data-testid={`invoice-open-${i.number}`} className="min-w-0 flex-1">
              <span className="font-mono text-sm text-gold">#{i.number}</span>
              <p className="mt-1 truncate text-sm text-slate-100">{i.customer_name} · manifest #{i.manifest_number}</p>
              <p className="truncate text-xs text-slate-500">
                {i.customer_email || "no email"} · due {String(i.due_at).slice(0, 10)}
              </p>
            </Link>
            <div className="flex items-center gap-3">
              <span className="font-mono text-sm text-slate-200">{money(i.amount)}</span>
              <span
                data-testid={`invoice-status-${i.number}`}
                className={`rounded-full border px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${
                  i.status === "paid" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-amber-500/40 bg-amber-500/10 text-amber-300"
                }`}
              >
                {i.status}
              </span>
              <Button
                data-testid={`invoice-send-${i.number}`}
                variant="outline"
                size="sm"
                className="border-ink-600 bg-transparent text-slate-300 hover:text-gold"
                onClick={async () => {
                  setMsg("");
                  try {
                    await api.post(`/invoices/${i.id}/send`);
                    setMsg(`Invoice #${i.number} emailed to ${i.customer_email}.`);
                  } catch (e) { setError(errMsg(e)); }
                }}
              >
                <Send className="mr-2 h-3.5 w-3.5" /> Send
              </Button>
              {i.status !== "paid" && (
                <Button
                  data-testid={`invoice-paid-${i.number}`}
                  size="sm"
                  className="bg-gold text-ink-900 hover:bg-gold-light"
                  onClick={async () => { await api.post(`/invoices/${i.id}/mark-paid`); load(); }}
                >
                  <CheckCircle2 className="mr-2 h-3.5 w-3.5" /> Mark paid
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
